import React, { useEffect, useMemo, useState, useRef} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import GamifiedButton from '../components/common/GamifiedButton';
import ReviewsSection from '../components/Reviews/ReviewsSection';
import { useCart } from '../contexts/CartContext';
import { useLang } from '../i18n/LanguageContext';
import { formaterPrix } from '../lib/prix';
import SecretDesParents from '../components/SecretDesParents';
import { getProduct, getProducts } from '../services/api';
import { packPhoto } from '../lib/packPhotos';

/**
 * Fiche produit — /product/:id
 *
 * Ce fichier était un stub de 8 lignes qui renvoyait vers /shop, et aucun lien
 * du site n'y menait. Résultat : personne ne pouvait consulter le détail de ce
 * qu'il achetait. C'est désormais LA page de référence du produit.
 *
 * Tout ce qui est affiché ici est vérifiable sur la boîte réelle : 2 à 10
 * joueurs, 20 minutes, 11 pièces + 1 pièce à gratter, avertissement 0-3 ans,
 * CE / EN 71, carton FSC, QR code renvoyant aux règles complètes.
 */

/** Le pack contient-il les accessoires (pochons, questionnaire, enveloppe) ? */
function packAvecAccessoires(produit) {
  return (produit?.features || []).some((f) => /pochon|questionnaire|enveloppe/i.test(String(f)));
}

/**
 * Classes de colonnes écrites EN ENTIER : Tailwind lit le source de façon
 * statique et ne génère pas une classe construite à la volée.
 */
const COLONNES_VIGNETTES = {
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
};

/**
 * Visuels du pack. Le grattage ouvre la galerie : c'est le moment que l'on
 * achète, et le visiteur vient de voir la boîte dans la boutique une seconde
 * plus tôt — la lui remontrer en premier n'apprend rien. La boîte et son dos
 * viennent du fichier d'impression (vectoriel, net, sans reflet) ; tout le
 * reste est photographié tel qu'expédié. largeur/hauteur évitent le saut de
 * mise en page quand on passe d'un format à l'autre.
 *
 * La galerie était la même pour tous : la fiche du Pack Essentiel montrait
 * donc les pochons et le questionnaire… qu'il ne contient pas. Un colis
 * « plus vide que la photo » se paie en avis à 3 étoiles.
 *
 * Fonction de t (règle i18n) : les textes dépendent de la langue active.
 */
function visuelsDuPack(produit, t) {
  const boite = {
    src: '/photos/boite-face.webp',
    largeur: 1000,
    hauteur: 1417,
    alt: t('fiche.visuels.boiteAlt'),
    legende: t('fiche.visuels.boiteLegende'),
  };
  const dos = {
    src: '/photos/boite-dos.webp',
    largeur: 1000,
    hauteur: 1455,
    alt: t('fiche.visuels.dosAlt'),
    legende: t('fiche.visuels.dosLegende'),
  };
  // La photo du grattage vaut pour TOUS les packs : c'est le moment du jeu,
  // et c'est la première vraie photo de la pièce à gratter (les rendus IA
  // n'en montraient pas).
  const grattage = {
    src: '/photos/grattage.webp',
    largeur: 768,
    hauteur: 1364,
    alt: t('fiche.visuels.grattageAlt'),
    legende: t('fiche.visuels.grattageLegende'),
  };
  // Le puzzle assemblé et encadré : ce qu'il reste une fois la fête passée,
  // et le seul visuel qui montre le résultat plutôt que le contenu. Vaut pour
  // tous les packs, les deux contiennent le puzzle. La douzième pièce reste
  // grise sur ces photos : aucun spoiler pour les futurs parents.
  const puzzleEncadre = {
    src: '/photos/puzzle-encadre.webp',
    largeur: 1200,
    hauteur: 1200,
    alt: t('fiche.visuels.puzzleEncadreAlt'),
    legende: t('fiche.visuels.puzzleEncadreLegende'),
  };
  const puzzleTable = {
    src: '/photos/puzzle-table.webp',
    largeur: 1200,
    hauteur: 1490,
    alt: t('fiche.visuels.puzzleTableAlt'),
    legende: t('fiche.visuels.puzzleTableLegende'),
  };
  const accessoires = [
    {
      src: '/photos/contenu.webp',
      largeur: 1200,
      hauteur: 1500,
      alt: t('fiche.visuels.contenuAlt'),
      legende: t('fiche.visuels.contenuLegende'),
    },
    {
      src: '/photos/pochons.webp',
      largeur: 1200,
      hauteur: 1855,
      alt: t('fiche.visuels.pochonsAlt'),
      legende: t('fiche.visuels.pochonsLegende'),
    },
    {
      src: '/photos/questionnaire.webp',
      largeur: 1200,
      hauteur: 1718,
      alt: t('fiche.visuels.questionnaireAlt'),
      legende: t('fiche.visuels.questionnaireLegende'),
    },
  ];
  return packAvecAccessoires(produit)
    ? [grattage, boite, puzzleEncadre, puzzleTable, ...accessoires, dos]
    : [grattage, boite, puzzleEncadre, puzzleTable, dos];
}

/**
 * Les trois accessoires du Pack Premium, décrits une bonne fois. L'enveloppe
 * souvenir n'a pas encore de photo dédiée : on la décrit, on n'invente pas
 * d'image.
 */
const accessoiresPremium = (t) => [
  {
    icone: '🎀',
    titre: t('fiche.accessoires.pochonsTitre'),
    texte: t('fiche.accessoires.pochonsTexte'),
  },
  {
    icone: '📝',
    titre: t('fiche.accessoires.questionnaireTitre'),
    texte: t('fiche.accessoires.questionnaireTexte'),
  },
  {
    icone: '✉️',
    titre: t('fiche.accessoires.enveloppeTitre'),
    texte: t('fiche.accessoires.enveloppeTexte'),
  },
];

const caracteristiques = (t) => [
  {
    icone: '👥',
    label: t('fiche.caracteristiques.joueursLabel'),
    valeur: t('fiche.caracteristiques.joueursValeur'),
  },
  {
    icone: '⏱️',
    label: t('fiche.caracteristiques.dureeLabel'),
    valeur: t('fiche.caracteristiques.dureeValeur'),
  },
  {
    icone: '🧩',
    label: t('fiche.caracteristiques.piecesLabel'),
    valeur: t('fiche.caracteristiques.piecesValeur'),
  },
  {
    icone: '🎂',
    label: t('fiche.caracteristiques.ageLabel'),
    valeur: t('fiche.caracteristiques.ageValeur'),
  },
];

const specifications = (t) => [
  [t('fiche.specs.contenuLabel'), t('fiche.specs.contenuValeur')],
  [t('fiche.specs.principeLabel'), t('fiche.specs.principeValeur')],
  [t('fiche.specs.reglesLabel'), t('fiche.specs.reglesValeur')],
  [t('fiche.specs.matiereLabel'), t('fiche.specs.matiereValeur')],
  [t('fiche.specs.puzzleLabel'), t('fiche.specs.puzzleValeur')],
  [t('fiche.specs.securiteLabel'), t('fiche.specs.securiteValeur')],
  [t('fiche.specs.origineLabel'), t('fiche.specs.origineValeur')],
];

// Même format que le reste du site (boutique, panier, paiement). Le passage
// à la virgule française se fera partout d'un coup, pas seulement ici.
export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { lang, t, tp, tpList } = useLang();
  const prix = (v) => formaterPrix(v, lang);

  const [produit, setProduit] = useState(null);

  // Barre d'achat mobile : visible quand le vrai bouton est sorti de l'écran,
  // qu'on a déjà défilé un peu (le premier écran reste au produit) et qu'on
  // n'est pas sur le pied de page (sinon elle en cacherait les liens).
  const ctaRef = useRef(null);
  const [ctaVisible, setCtaVisible] = useState(true);
  const [piedVisible, setPiedVisible] = useState(false);
  const [aDefile, setADefile] = useState(false);
  useEffect(() => {
    if (!produit || typeof IntersectionObserver === 'undefined') return undefined;
    const cible = ctaRef.current;
    const pied = document.querySelector('footer');
    const observateur = new IntersectionObserver((entrees) => {
      for (const e of entrees) {
        if (e.target === cible) setCtaVisible(e.isIntersecting);
        if (e.target === pied) setPiedVisible(e.isIntersecting);
      }
    });
    if (cible) observateur.observe(cible);
    if (pied) observateur.observe(pied);
    const surDefilement = () => setADefile(window.scrollY > window.innerHeight * 0.6);
    surDefilement();
    window.addEventListener('scroll', surDefilement, { passive: true });
    return () => {
      observateur.disconnect();
      window.removeEventListener('scroll', surDefilement);
    };
  }, [produit]);
  const barreVisible = aDefile && !ctaVisible && !piedVisible;
  const [catalogue, setCatalogue] = useState([]);
  const [etat, setEtat] = useState('chargement'); // chargement | ok | introuvable | erreur
  const [visuel, setVisuel] = useState(0);
  const [genre, setGenre] = useState(null);
  const [alerteGenre, setAlerteGenre] = useState(false);

  useEffect(() => {
    let annule = false;
    setEtat('chargement');

    getProduct(id)
      .then((p) => {
        if (annule) return;
        setProduit(p);
        setEtat('ok');
      })
      .catch((err) => {
        if (annule) return;
        // On distingue vraiment les deux cas : un 404 mérite « ce pack n'existe
        // plus », une panne réseau mérite « réessayez ».
        setEtat(err?.response?.status === 404 ? 'introuvable' : 'erreur');
      });

    return () => {
      annule = true;
    };
  }, [id]);

  // Catalogue complet, uniquement pour proposer le pack supérieur. Silencieux :
  // son absence ne doit pas empêcher d'acheter.
  useEffect(() => {
    getProducts()
      .then((liste) => setCatalogue(Array.isArray(liste) ? liste : []))
      .catch(() => {});
  }, []);

  const puces = useMemo(
    () => (Array.isArray(produit?.features) ? produit.features.filter(Boolean) : []),
    [produit]
  );

  // t change avec la langue : la galerie se retraduit au changement de langue.
  const visuels = useMemo(() => visuelsDuPack(produit, t), [produit, t]);

  // Le pack juste au-dessus en prix : c'est lui qu'on met en avant.
  const packSuperieur = useMemo(() => {
    if (!produit) return null;
    return catalogue
      .filter((p) => p.id !== produit.id && Number(p.price) > Number(produit.price))
      .sort((a, b) => Number(a.price) - Number(b.price))[0] || null;
  }, [catalogue, produit]);

  // Respect de « mouvement réduit » : on n'impose pas une boucle vidéo à
  // quelqu'un qui a demandé le contraire. Lecture au premier rendu (le
  // pré-rendu tourne sous Node, d'où le garde sur window).
  const [animationOk, setAnimationOk] = useState(
    () => typeof window !== 'undefined' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const suivre = (e) => setAnimationOk(!e.matches);
    mq.addEventListener('change', suivre);
    return () => mq.removeEventListener('change', suivre);
  }, []);

  const demandeGenre = !produit?.variantType || produit.variantType === 'standard';

  const ajouter = () => {
    if (!produit) return;

    if (demandeGenre && !genre) {
      setAlerteGenre(true);
      document.getElementById('choix-genre')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const variante = demandeGenre ? genre : produit.variantType;
    const etiquette =
      variante === 'mixed'
        ? t('fiche.genre.mixte')
        : variante === 'boy'
          ? t('fiche.genre.garcon')
          : t('fiche.genre.fille');

    addToCart({
      id: produit.id,
      name: `Joyful Reveal - Ours - ${tp(produit, 'name', produit.name)} (${etiquette})`,
      price: Number(produit.price),
      image: packPhoto(produit),
      weight: Number(produit.weight) || 1.5,
      variant: variante,
      language: 'FR',
    });
    navigate('/cart');
  };

  // Schema.org : une fiche produit sans balisage n'a aucune chance d'obtenir
  // une fiche enrichie dans Google. Volontairement sur les valeurs de la base
  // (métadonnées, pas de l'affichage).
  useEffect(() => {
    if (!produit) return;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: `Joyful Reveal — ${produit.name}`,
      description: produit.longDescription || produit.description || '',
      image: `https://www.mirumgames.com${packPhoto(produit)}`,
      brand: { '@type': 'Brand', name: 'Mirum Games' },
      offers: {
        '@type': 'Offer',
        priceCurrency: 'EUR',
        price: Number(produit.price).toFixed(2),
        availability:
          Number(produit.stockQuantity) > 0
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
        url: `https://www.mirumgames.com/product/${produit.id}`,
      },
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, [produit]);

  if (etat === 'chargement') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-[#F7F4EE]">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-[#4AA294]/25 border-t-[#2F7E72]" />
          <p className="mt-4 text-[#33403B]/70">{t('fiche.chargement.produit')}</p>
        </div>
      </div>
    );
  }

  if (etat !== 'ok') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-[#F7F4EE] px-5">
        <div className="max-w-[520px] text-center">
          {/* « introuvable » (404 de l'API) = le produit n'existe pas, on le
              retire de l'index. « erreur » = notre API n'a pas répondu : la
              fiche existe toujours. La désindexer pour un incident réseau —
              et des 403 Cloudflare intermittents sont déjà arrivés sur
              api.mirumgames.com — ferait disparaître de Google les deux seules
              pages qui vendent, pour des semaines. */}
          <SEO
            title={etat === 'introuvable' ? t('fiche.seo.titreIntrouvable') : t('fiche.seo.titreErreur')}
            description={t('fiche.seo.descriptionErreur')}
            noindex={etat === 'introuvable'}
          />
          <p className="m-0 mb-3 text-5xl" aria-hidden="true">
            {etat === 'introuvable' ? '🧩' : '📡'}
          </p>
          <h1 className="m-0 mb-3 text-2xl font-black uppercase tracking-tight text-[#33403B]">
            {etat === 'introuvable' ? t('fiche.erreur.introuvableTitre') : t('fiche.erreur.erreurTitre')}
          </h1>
          <p className="m-0 mb-6 text-[15px] text-[#33403B]/75">
            {etat === 'introuvable'
              ? t('fiche.erreur.introuvableTexte')
              : t('fiche.erreur.erreurTexte')}
          </p>
          <Link to="/shop" className="no-underline">
            <GamifiedButton variant="primary" size="lg" data-cta="fiche:erreur-vers-boutique">
              {t('fiche.erreur.versBoutique')}
            </GamifiedButton>
          </Link>
        </div>
      </div>
    );
  }

  const enStock = Number(produit.stockQuantity) > 0;
  const remise =
    produit.originalPrice && Number(produit.originalPrice) > Number(produit.price)
      ? Math.round(
          ((Number(produit.originalPrice) - Number(produit.price)) / Number(produit.originalPrice)) *
            100
        )
      : null;

  return (
    <div className="bg-[#F7F4EE]">
      <SEO
        title={t('fiche.seo.titre', { nom: tp(produit, 'name', produit.name) })}
        description={
          tp(produit, 'longDescription', produit.longDescription)?.slice(0, 155) ||
          tp(produit, 'description', produit.description) ||
          t('fiche.seo.descriptionDefaut')
        }
        url={`/product/${produit.id}`}
        image={packPhoto(produit)}
        type="product"
        keywords="gender reveal, jeu gender reveal, puzzle révélation sexe bébé, gender reveal party"
      />

      {/* Fil d'Ariane */}
      <nav aria-label={t('fiche.ariane.label')} className="mx-auto max-w-[1200px] px-5 pt-4 text-[13px] lg:pt-6">
        <ol className="m-0 flex list-none flex-wrap items-center gap-2 p-0 text-[#33403B]/60">
          <li>
            <Link to="/" className="no-underline hover:text-[#2F7E72]">{t('nav.home')}</Link>
          </li>
          <li aria-hidden="true">›</li>
          <li>
            <Link to="/shop" className="no-underline hover:text-[#2F7E72]">{t('nav.shop')}</Link>
          </li>
          <li aria-hidden="true">›</li>
          <li className="font-semibold text-[#33403B]" aria-current="page">
            {tp(produit, 'name', produit.name)}
          </li>
        </ol>
      </nav>

      {/* Bloc principal : visuels + achat */}
      <section className="mx-auto grid max-w-[1200px] gap-x-10 gap-y-6 px-5 py-5 lg:grid-cols-2 lg:gap-x-14 lg:py-8">
        {/* Titre, promesse et prix : en premier sur mobile, en haut de la
            colonne de droite sur grand écran. */}
        <div className="flex min-w-0 flex-col gap-6 lg:col-start-2 lg:row-start-1">
          <div>
            <p className="m-0 mb-2 text-xs font-bold uppercase tracking-[0.2em] text-[#4AA294]">
              {t('fiche.achat.gamme')}
            </p>
            <h1 className="m-0 mb-3 text-3xl font-black uppercase tracking-tight text-[#33403B] md:text-4xl">
              {tp(produit, 'name', produit.name)}
            </h1>
            <p className="m-0 mt-3 text-[17px] font-semibold leading-snug text-[#17453E]">
              {t('fiche.achat.promesse')}
            </p>
            {produit.tagline && (
              <p className="m-0 text-[15px] font-semibold text-[#2F7E72]">
                {tp(produit, 'tagline', produit.tagline)}
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-baseline gap-3">
            <span className="text-4xl font-black text-[#2F7E72]">{prix(produit.price)}</span>
            {remise && (
              <>
                <span className="text-lg text-[#33403B]/45 line-through">
                  {prix(produit.originalPrice)}
                </span>
                <span className="rounded-lg bg-[#C44569] px-2.5 py-1 text-xs font-bold text-white">
                  {t('fiche.achat.remise', { n: remise })}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Visuels */}
        <div className="min-w-0 lg:col-start-1 lg:row-start-1 lg:row-span-2">
          <div className="overflow-hidden rounded-2xl border-2 border-[#4AA294]/20 bg-white p-4 shadow-sm">
            <img
              key={visuels[visuel].src}
              src={visuels[visuel].src}
              alt={visuels[visuel].alt}
              className="mx-auto max-h-[46vh] w-auto max-w-full rounded-xl object-contain lg:max-h-[520px]"
              width={visuels[visuel].largeur}
              height={visuels[visuel].hauteur}
              loading="eager"
              decoding="async"
            />
          </div>

          {/* Classes écrites en entier : Tailwind lit le source de façon
              statique et ne générerait pas une classe construite à la volée. */}
          <div className={`mt-4 flex min-w-0 snap-x gap-3 overflow-x-auto pb-2 [scrollbar-width:thin] lg:grid lg:overflow-visible lg:pb-0 ${COLONNES_VIGNETTES[visuels.length] || 'grid-cols-4'}`}>
            {visuels.map((v, i) => (
              <button
                key={v.src}
                type="button"
                onClick={() => setVisuel(i)}
                aria-label={t('fiche.visuels.voir', { legende: v.legende })}
                aria-current={i === visuel}
                className={`w-24 shrink-0 snap-start overflow-hidden rounded-xl border-2 bg-white p-1 transition-all focus:outline-none lg:w-auto focus-visible:ring-4 focus-visible:ring-[#EDB82F] ${
                  i === visuel
                    ? 'border-[#2F7E72] shadow-md'
                    : 'border-[#4AA294]/20 hover:border-[#4AA294]/60'
                }`}
              >
                <img
                  src={v.src.replace('.webp', '-600.webp')}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  decoding="async"
                  className="h-20 w-full rounded-lg object-cover"
                />
              </button>
            ))}
          </div>
          <p className="mt-2 text-center text-xs text-[#33403B]/60">{visuels[visuel].legende}</p>
        </div>

        {/* Achat */}
        <div className="flex min-w-0 flex-col gap-6 lg:col-start-2 lg:row-start-2">
          <p className="m-0 text-[15px] leading-relaxed text-[#33403B]">
            {tp(produit, 'longDescription', produit.longDescription) ||
              tp(produit, 'description', produit.description)}
          </p>

          {/* Contenu du pack */}
          {puces.length > 0 && (
            <div className="rounded-2xl border-2 border-[#4AA294]/25 bg-white p-5">
              <h2 className="m-0 mb-3 text-sm font-black uppercase tracking-wide text-[#33403B]">
                {t('fiche.achat.contenuTitre')}
              </h2>
              <ul className="m-0 list-none space-y-2 p-0">
                {tpList(produit, 'features', puces).map((f, i) => (
                  <li key={i} className="flex gap-2 text-[14px] leading-relaxed text-[#33403B]">
                    <span className="text-[#2F7E72]" aria-hidden="true">✓</span>
                    <span>{String(f).replace(/^[✓•-]\s*/, '')}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Choix fille / garçon */}
          {demandeGenre ? (
            <div id="choix-genre">
              <h2 className="m-0 mb-3 text-sm font-black uppercase tracking-wide text-[#33403B]">
                {t('fiche.genre.titre')}
              </h2>
              <div className="flex gap-3">
                {[
                  { cle: 'girl', emoji: '👧', mot: t('fiche.genre.fille') },
                  { cle: 'boy', emoji: '👦', mot: t('fiche.genre.garcon') },
                ].map((g) => (
                  <button
                    key={g.cle}
                    type="button"
                    onClick={() => {
                      setGenre(g.cle);
                      setAlerteGenre(false);
                    }}
                    aria-pressed={genre === g.cle}
                    className={`flex flex-1 items-center justify-center gap-2 rounded-xl border-2 py-3 transition-all focus:outline-none focus-visible:ring-4 focus-visible:ring-[#EDB82F] ${
                      genre === g.cle
                        ? 'border-[#2F7E72] bg-[#4AA294]/10 text-[#17453E] shadow-md'
                        : 'border-[#4AA294]/25 text-[#33403B]/70 hover:border-[#4AA294]/60'
                    }`}
                  >
                    <span className="text-xl" aria-hidden="true">{g.emoji}</span>
                    <span className="text-sm font-bold uppercase tracking-wide">{g.mot}</span>
                  </button>
                ))}
              </div>
              {alerteGenre && (
                <p role="alert" className="m-0 mt-2 text-[13px] font-semibold text-[#C44569]">
                  {t('fiche.genre.alerte')}
                </p>
              )}
              <p className="m-0 mt-2 text-xs text-[#33403B]/60">
                {t('fiche.genre.discretion')}
              </p>
            </div>
          ) : (
            <div className="rounded-xl border-2 border-[#4AA294]/25 bg-white p-4 text-center">
              <p className="m-0 text-sm font-bold text-[#17453E]">
                {produit.variantType === 'mixed'
                  ? t('fiche.genre.packMixte')
                  : produit.variantType === 'boy'
                    ? t('fiche.genre.packGarcon')
                    : t('fiche.genre.packFille')}
              </p>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <div ref={ctaRef} aria-hidden="true" className="h-px w-full" />
            <GamifiedButton
              variant="primary"
              size="lg"
              onClick={ajouter}
              disabled={!enStock}
              data-cta={`fiche:ajouter-${produit.id}`}
            >
              {enStock ? t('fiche.achat.ajouter') : t('fiche.achat.indisponible')}
            </GamifiedButton>
            {/* Une fête a une date : sans délai ni prix de port affiches ici,
                l'acheteuse doit aller jusqu'à l'étape 2 du paiement pour savoir
                si le colis arrivera à temps et combien il coûte vraiment. */}
            {enStock ? (
              <ul className="m-0 list-none space-y-1.5 p-0 text-[13px] leading-relaxed text-[#33403B]/75">
                <li>{t('fiche.achat.expedition')}</li>
                <li>{t('fiche.achat.livraison')}</li>
                <li>{t('fiche.achat.langue')}</li>
                <li>{t('fiche.achat.retour')}</li>
              </ul>
            ) : (
              <p className="m-0 text-center text-[13px] text-[#33403B]/65">
                {t('fiche.achat.reappro')}
              </p>
            )}
            {/* Le rêve est cinq écrans plus bas ; on tend la main d'ici. */}
            <a
              href="#souvenir"
              data-cta="fiche:vers-souvenir"
              className="mt-4 inline-flex items-center gap-1 text-[14px] font-semibold text-[#2F7E72] no-underline hover:underline"
            >
              {t('fiche.souvenir.surtitre')} {t('fiche.souvenir.titre')} ↓
            </a>
          </div>

          {/* Le pack au-dessus : c'est ici que se joue le choix */}
          {packSuperieur && (
            <div className="rounded-2xl border-2 border-[#EDB82F] bg-[#FFF8E6] p-5">
              <p className="m-0 mb-1 text-xs font-black uppercase tracking-wide text-[#B98A0F]">
                {t('fiche.upsell.badge')}
              </p>
              <p className="m-0 mb-3 text-[14px] leading-relaxed text-[#33403B]">
                {t('fiche.upsell.phraseAvant')}{' '}
                <strong>
                  {t('fiche.upsell.phraseGras', {
                    prix: prix(Number(packSuperieur.price) - Number(produit.price)),
                  })}
                </strong>
                {t('fiche.upsell.phraseMilieu')}{' '}
                <strong>{tp(packSuperieur, 'name', packSuperieur.name)}</strong>{' '}
                {t('fiche.upsell.phraseApres')}
              </p>
              <Link
                to={`/product/${packSuperieur.id}`}
                data-cta={`fiche:vers-${packSuperieur.id}`}
                className="inline-flex items-center gap-2 rounded-lg bg-[#2F7E72] px-4 py-2.5 text-sm font-bold text-white no-underline transition-all hover:-translate-y-0.5 hover:bg-[#17453E]"
              >
                {t('fiche.upsell.voir', { nom: tp(packSuperieur, 'name', packSuperieur.name) })}
              </Link>
            </div>
          )}

          {/* Caractéristiques rapides */}
          <dl className="m-0 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {caracteristiques(t).map((c) => (
              <div
                key={c.label}
                className="rounded-xl border-2 border-[#4AA294]/20 bg-white p-3 text-center"
              >
                <span className="block text-2xl" aria-hidden="true">{c.icone}</span>
                <dt className="mt-1 text-[11px] font-bold uppercase tracking-wide text-[#4AA294]">
                  {c.label}
                </dt>
                <dd className="m-0 text-[13px] font-semibold text-[#33403B]">{c.valeur}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Le scénario de la soirée — mêmes textes que l'accueil (how.*) : une
          seule version circule dans les six langues. */}
      <section className="px-5 py-10">
        <div className="mx-auto max-w-[900px] rounded-2xl border-2 border-[#4AA294]/20 bg-white p-5 shadow-sm sm:p-7">
          <p className="m-0 mb-2 text-xs font-bold uppercase tracking-[0.2em] text-[#4AA294]">
            {t('how.badge')}
          </p>
          <h2 className="m-0 mb-3 text-xl font-black uppercase tracking-tight text-[#33403B]">
            {t('how.titre')}
          </h2>
          <p className="m-0 mb-6 text-[15px] leading-relaxed text-[#33403B]/75">
            {t('how.description')}
          </p>
          <ol className="m-0 list-none space-y-4 p-0">
            {[1, 2, 3, 4].map((n) => (
              <li key={n} className="flex gap-3 text-[15px] leading-relaxed text-[#33403B]">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#EDB82F] to-[#E1A72E] text-sm font-black text-white">
                  {n}
                </span>
                <span>
                  <strong className="block font-black">{t(`how.etape${n}.titre`)}</strong>
                  {t(`how.etape${n}.texte`)}
                </span>
              </li>
            ))}
          </ol>
          <SecretDesParents className="mt-6" />
          <p className="m-0 mt-5 text-sm text-[#33403B]/75">
            <Link
              to="/regles-de-jeu"
              data-cta="fiche:vers-regles"
              className="font-bold text-[#2F7E72] underline decoration-[#4AA294]/40 underline-offset-4 hover:text-[#17453E]"
            >
              {t('fiche.etapes.reglesLien')}
            </Link>{' '}
            {t('fiche.etapes.reglesSuite')}
          </p>
        </div>
      </section>

      {/* Les accessoires du Pack Premium, photos réelles à l'appui. Sur la
          fiche Essentiel, la même section devient l'argument d'upsell. */}
      <section className="px-5 py-10">
        <div className="mx-auto max-w-[900px] rounded-2xl border-2 border-[#4AA294]/20 bg-white p-5 shadow-sm sm:p-7">
          <h2 className="m-0 mb-2 text-xl font-black uppercase tracking-tight text-[#33403B]">
            {packAvecAccessoires(produit)
              ? t('fiche.accessoires.titreAvec')
              : t('fiche.accessoires.titreSans')}
          </h2>
          <p className="m-0 mb-6 text-[15px] leading-relaxed text-[#33403B]/75">
            {packAvecAccessoires(produit)
              ? t('fiche.accessoires.introAvec')
              : t('fiche.accessoires.introSans')}
          </p>

          <div className="mb-6 grid gap-4 sm:grid-cols-2">
            <figure className="m-0">
              <img
                src="/photos/contenu-600.webp"
                alt={t('fiche.accessoires.photoContenuAlt')}
                loading="lazy"
                className="w-full rounded-xl border border-[#4AA294]/20 object-cover"
              />
              <figcaption className="mt-2 text-center text-[13px] font-semibold text-[#33403B]/60">
                {t('fiche.accessoires.photoContenuLegende')}
              </figcaption>
            </figure>
            <figure className="m-0">
              <img
                src="/photos/pochons-600.webp"
                alt={t('fiche.accessoires.photoPochonsAlt')}
                loading="lazy"
                className="w-full rounded-xl border border-[#4AA294]/20 object-cover"
              />
              <figcaption className="mt-2 text-center text-[13px] font-semibold text-[#33403B]/60">
                {t('fiche.accessoires.photoPochonsLegende')}
              </figcaption>
            </figure>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            {accessoiresPremium(t).map((a) => (
              <div key={a.titre} className="rounded-xl bg-[#F7F4EE] p-4">
                <p className="m-0 mb-1 text-lg" aria-hidden="true">
                  {a.icone}
                </p>
                <h3 className="m-0 mb-1 text-[15px] font-black text-[#33403B]">{a.titre}</h3>
                <p className="m-0 text-[13px] leading-relaxed text-[#33403B]/75">{a.texte}</p>
              </div>
            ))}
          </div>

          {!packAvecAccessoires(produit) && packSuperieur && (
            <p className="m-0 mt-6 text-center">
              <Link
                to={`/product/${packSuperieur.id}`}
                data-cta="fiche:accessoires-vers-premium"
                className="inline-block rounded-xl bg-[#17453E] px-6 py-3 text-sm font-black text-white no-underline transition-all hover:-translate-y-0.5 hover:shadow-lg"
              >
                {t('fiche.accessoires.decouvrir', {
                  nom: tp(packSuperieur, 'name', packSuperieur.name),
                })}
              </Link>
            </p>
          )}
        </div>
      </section>

      {/* Le souvenir : le puzzle au format cadre, le questionnaire au dos.
          14 × 21 cm est mesuré sur les fichiers d'impression — pas un slogan. */}
      <section id="souvenir" className="px-5 pb-10">
        <div className="mx-auto max-w-[900px] overflow-hidden rounded-2xl bg-gradient-to-br from-[#17453E] via-[#22403A] to-[#33403B] p-6 shadow-lg sm:p-9">
          <div className="grid items-center gap-8 md:grid-cols-[minmax(0,260px)_1fr]">
            <figure className="m-0 justify-self-center">
              {/* L'effet cadre est en CSS : passe-partout clair + baguette sombre.
                  Le visuel montrait un rendu avec des badges « TEAM » et une pièce
                  MIRUM qui n'existent pas dans la boîte ; il cède la place au
                  puzzle réellement encadré. Mouvement réduit : image fixe. */}
              {/* preload="metadata" et non "none" : « none » empêche la lecture
                  automatique sur Safari, et « metadata » ne télécharge pas la vidéo. */}
              {animationOk ? (
                <video
                  src="/videos/puzzle-encadre.mp4"
                  poster="/videos/puzzle-encadre.webp"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  aria-label={t('fiche.souvenir.photoAlt')}
                  className="w-full max-w-[260px] rounded-sm border-[10px] border-[#F7F4EE] shadow-[0_0_0_6px_#0F2622,0_22px_50px_rgba(0,0,0,0.45)]"
                />
              ) : (
                <img
                  src="/videos/puzzle-encadre.webp"
                  alt={t('fiche.souvenir.photoAlt')}
                  loading="lazy"
                  className="w-full max-w-[260px] rounded-sm border-[10px] border-[#F7F4EE] shadow-[0_0_0_6px_#0F2622,0_22px_50px_rgba(0,0,0,0.45)]"
                />
              )}
              <figcaption className="mt-3 text-center text-[12px] font-semibold text-white/50">
                {t('fiche.souvenir.photoLegende')}
              </figcaption>
            </figure>
            <div className="text-left">
              <p className="m-0 mb-2 text-xs font-bold uppercase tracking-[0.2em] text-[#EDB82F]">
                {t('fiche.souvenir.surtitre')}
              </p>
              <h2 className="m-0 mb-4 text-2xl font-black uppercase tracking-tight text-white">
                {t('fiche.souvenir.titre')}
              </h2>
              <p className="m-0 mb-3 text-[15px] leading-relaxed text-white/80">
                {t('fiche.souvenir.tailleAvant')}{' '}
                <strong className="text-white">14 × 21 cm</strong>{' '}
                {t('fiche.souvenir.tailleApres')}
              </p>
              {packAvecAccessoires(produit) ? (
                <p className="m-0 text-[15px] leading-relaxed text-white/80">
                  {t('fiche.souvenir.premiumAvant')}{' '}
                  <strong className="text-white">{t('fiche.souvenir.premiumEnveloppe')}</strong>
                  {t('fiche.souvenir.premiumApres')}
                </p>
              ) : (
                <p className="m-0 text-[15px] leading-relaxed text-white/80">
                  {t('fiche.souvenir.upsell')}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Fiche technique */}
      <section className="px-5 pb-12">
        <div className="mx-auto max-w-[900px] rounded-2xl border-2 border-[#4AA294]/20 bg-white p-5 shadow-sm sm:p-7">
          <h2 className="m-0 mb-5 text-xl font-black uppercase tracking-tight text-[#33403B]">
            {t('fiche.specs.titre')}
          </h2>
          <dl className="m-0 divide-y divide-[#4AA294]/15">
            {specifications(t).map(([label, valeur]) => (
              <div key={label} className="grid gap-1 py-3 sm:grid-cols-[190px_1fr] sm:gap-4">
                <dt className="text-[13px] font-bold uppercase tracking-wide text-[#4AA294]">
                  {label}
                </dt>
                <dd className="m-0 text-[14px] leading-relaxed text-[#33403B]">{valeur}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <ReviewsSection productId={1} />

      {/* Barre d'achat mobile. Le bouton est à deux écrans du haut et la
          section souvenir à près de six : qui a envie d'acheter en lisant
          « il reste un tableau » ne doit pas remonter 3 000 px pour le faire.
          Rendue seulement quand elle est utile, donc jamais focalisable en
          dehors de l'écran. */}
      {barreVisible && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t-2 border-[#4AA294]/30 bg-white/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 shadow-[0_-6px_20px_rgba(0,0,0,0.12)] backdrop-blur animate-[barre-achat-monter_0.3s_ease-out] lg:hidden">
          <div className="mx-auto flex max-w-[600px] items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="m-0 truncate text-[12px] font-semibold uppercase tracking-wide text-[#33403B]/60">
                {tp(produit, 'name', produit.name)}
              </p>
              <p className="m-0 text-xl font-black text-[#2F7E72]">{prix(produit.price)}</p>
            </div>
            <GamifiedButton
              variant="primary"
              size="md"
              onClick={ajouter}
              disabled={!enStock}
              data-cta={`fiche:ajouter-barre-${produit.id}`}
            >
              {enStock ? t('fiche.achat.ajouter') : t('fiche.achat.indisponible')}
            </GamifiedButton>
          </div>
        </div>
      )}
    </div>
  );
}
